// ─── TypeScript Types for KAELO Dashboard ────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  office_id: string;
  is_verified?: boolean;
}

export type OfficeId = 'home' | 'kaalvoet' | 'zikhethele' | 'developments' | 'odimo';

export interface OfficeConfig {
  id: OfficeId;
  name: string;
  color: string;
  description: string;
  hqApps: OfficePanelId[];
}

export type HQPanelId =
  | 'chat'
  | 'offices'
  | 'kanban'
  | 'gantt'
  | 'calendar'
  | 'reports'
  | 'registry'
  | 'notifications'
  | 'settings'
  | 'users';

export type OfficePanelId =
  | 'chat'
  | 'kanban'
  | 'gantt'
  | 'team'
  | 'calendar'
  | 'budget'
  | 'projects'
  | 'sacap'
  | 'deals'
  | 'leads'
  | 'hopper'
  | 'feasibility'
  | 'windsor'
  | 'properties'
  | 'tenants'
  | 'compliance';

export type NavState =
  | { view: 'hq'; panel: HQPanelId }
  | { view: 'office'; officeId: OfficeId; panel: OfficePanelId }
  | { view: 'agent'; agentId: string; officeId?: OfficeId };

export interface Agent {
  name: string;
  type: string;
  model: string;
  description: string;
  skills: string[] | { reference: string };
  version?: string;
  officeId?: OfficeId;
  status?: 'active' | 'idle' | 'blocked' | 'offline';
  system_prompt?: string;
}

export type TaskColumn = 'todo' | 'inprogress' | 'review' | 'done';

export interface Task {
  id: string;
  title: string;
  description?: string;
  office_id: string;
  agent_name?: string;
  column: TaskColumn;
  start_date?: string;
  end_date?: string;
  dependencies: string[];
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  type: 'blocker' | 'approval' | 'override' | 'error';
  title: string;
  description: string;
  office_id?: string;
  agent_name?: string;
  status: 'pending' | 'approved' | 'rejected' | 'dismissed';
  created_at: string;
}

export type RegistryStatus = 'INBOX' | 'QUEUED' | 'DONE' | 'REJECTED';

export interface RegistryItem {
  id: string;
  name: string;
  category: string;
  status: RegistryStatus;
  effort: 'low' | 'medium' | 'high';
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface CalendarBlock {
  id: string;
  title: string;
  date: string;
  time_start?: string;
  time_end?: string;
  protection_level: 'hard' | 'soft' | 'open';
  override_requested: boolean;
  override_reason?: string;
}

export interface ChatMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
}

export interface Settings {
  githubPat: string;
  hq: {
    chatWebhookUrl: string;
    chatPollUrl: string;
    tasksApiUrl: string;
    notificationsApiUrl: string;
  };
  offices: Record<string, {
    chatWebhookUrl?: string;
    chatPollUrl?: string;
  }>;
  agents: Record<string, {
    chatWebhookUrl?: string;
    chatPollUrl?: string;
  }>;
  reports: {
    morningSummaryUrl: string;
    dailyIntelUrl: string;
    selfAuditUrl: string;
    agentPerformanceUrl: string;
  };
  /** Per-office app API endpoints. Key: `${officeId}.${appId}ApiUrl` */
  officeApps: Record<string, Record<string, string>>;
}

export const DEFAULT_SETTINGS: Settings = {
  githubPat: '',
  hq: {
    chatWebhookUrl: '',
    chatPollUrl: '',
    tasksApiUrl: '',
    notificationsApiUrl: '',
  },
  offices: {},
  agents: {},
  reports: {
    morningSummaryUrl: '',
    dailyIntelUrl: '',
    selfAuditUrl: '',
    agentPerformanceUrl: '',
  },
  officeApps: {},
};

// ─── Auth Request / Response Types ───────────────────────────────────────────

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface AcceptInviteRequest {
  email: string;
  name: string;
  password: string;
  token: string;
}

export interface SendInviteRequest {
  email: string;
  office_id: string;
}

export interface ResendVerificationRequest {
  email?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface AuthResponse {
  message: string;
}

export interface InviteResponse {
  message: string;
  invite_token: string;
}

export interface UserResponse extends User {
  created_at: string;
}
